import { Navigate } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import { useUserStore } from '@/stores/userStore'
import { evaluateUrl } from '@/lib/launchGating'
import { LoadingSpinner } from '@/components/shared/LoadingSpinner'

const ADMIN_HOME = '/admin/providers'

interface ProtectedRouteProps {
  children: React.ReactNode
  skipOnboardingCheck?: boolean
  /** Allow platform super-admins to access this route (default: false — platform admins get redirected to /admin/providers) */
  allowPlatformAdmin?: boolean
  /**
   * Plan 30 Lane 1 — allow anonymous (no-JWT) users to render this
   * route. The downstream component is responsible for branching on
   * useAuthStore.user (e.g. BuildPage.handleSend posts to
   * /api/anon/quick-build when user is null).
   */
  allowAnonymous?: boolean
}

export function ProtectedRoute({ children, skipOnboardingCheck, allowPlatformAdmin, allowAnonymous }: ProtectedRouteProps) {
  const user = useAuthStore((s) => s.user)
  const loading = useAuthStore((s) => s.loading)
  const isPlatformAdmin = useAuthStore((s) => s.isPlatformAdmin)
  const adminLoading = useAuthStore((s) => s.adminLoading)
  const profile = useUserStore((s) => s.profile)
  const profileLoaded = useUserStore((s) => s.profileLoaded)
  // NOTE: having a workspace no longer counts as "onboarded". Signup auto-creates
  // a default workspace (ADR-046) and the anon build lane lets people try a build
  // with no account — so post-signup we want EVERY user to complete onboarding,
  // not skip it just because a workspace exists. Onboarding is gated purely on
  // profile.onboarding_completed (set by the wizard's profile PATCH on completion;
  // existing users were grandfathered true via migration 20260616120000).

  // Auth loading — wait for initial session check
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-[#0a0a0a]">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  // Plan 30 — anonymous lane. No user, no JWT, but the route opts in
  // to the anon fast-build flow. Render the component immediately so
  // the BuildPage hero can offer a prompt input without forcing sign-up.
  if (!user && allowAnonymous) return <>{children}</>

  // No user → redirect to login (check before profileLoaded to avoid spinner on logout)
  if (!user) return <Navigate to="/login" replace />

  // Profile still loading — show spinner while fetching user data
  if (!profileLoaded || adminLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-[#0a0a0a]">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  // Platform super-admins should not access regular user routes — redirect to admin dashboard
  // ...but only to a page LaunchGate would let them reach. If the launch gate
  // refused /admin/providers it would send them back here, and the two redirects
  // would ping-pong forever (a blank page). In that case render this page.
  if (isPlatformAdmin && !allowPlatformAdmin) {
    if (evaluateUrl(ADMIN_HOME, undefined, true).result === 'allowed') {
      return <Navigate to={ADMIN_HOME} replace />
    }
  }

  // Redirect to the project + brand stepper until onboarding is completed.
  // Platform admins and routes that opt out (skipOnboardingCheck — e.g. the
  // onboarding route itself) are exempt.
  const isOnboarded = !!profile?.onboarding_completed
  if (!skipOnboardingCheck && !isPlatformAdmin && !isOnboarded) {
    return <Navigate to="/onboarding/setup" replace />
  }

  return <>{children}</>
}
